import { useCallback, useState, useEffect } from 'react';
import { DollarCircleOutlined } from '@ant-design/icons';
import { AModal, ASelect } from '../../../components';
import { IFinancial, IPaymentMethod } from '../../../shared/interfaces';
import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { Form, DatePicker, Divider, Typography } from 'antd';
import dayjs from 'dayjs';

interface IPaymentProps {
  open: boolean;
  close: () => void;
  selectRows: IFinancial[];
  setSelectRows: React.Dispatch<React.SetStateAction<IFinancial[]>>;
  paymentsMethods: IPaymentMethod[];
  setFinancials: React.Dispatch<React.SetStateAction<IFinancial[]>>;
}

const FinancialPayment: React.FC<IPaymentProps> = ({
  open,
  close,
  selectRows,
  setSelectRows,
  paymentsMethods,
  setFinancials,
}) => {
  const { addToast } = useToast();
  const [payment_date, setPayment_date] = useState<string>(
    new Date().toISOString().split('T')[0],
  );
  const [payment_method, setPayment_method] = useState<string>();

  const handleEditSubmit = useCallback(async () => {
    if (!payment_date || !payment_method) {
      addToast({
        type: 'warning',
        title: 'Atenção',
        description: 'Preencha a data e a forma de pagamento',
      });
      return;
    }

    try {
      const ids = selectRows.map((item) => String(item.id));
      const response = await api.patch(`/financials/payment`, {
        ids,
        payment_date,
        payment: String(payment_method ?? ''),
        updated_by: '',
      });

      // Atualiza a lista localmente
      const updatedItems = response.data;
      setFinancials((prev) =>
        prev.map((item) => {
          const updated = updatedItems.find(
            (u: IFinancial) => u.id === item.id,
          );
          return updated || item;
        }),
      );

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Pagamento/Baixa realizado com sucesso.',
      });
      setSelectRows([]);
      close();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          error.response?.data?.message || 'Erro ao processar pagamento',
      });
    }
  }, [
    payment_date,
    payment_method,
    selectRows,
    setFinancials,
    addToast,
    close,
    setSelectRows,
  ]);

  useEffect(() => {
    if (open && selectRows.length === 1) {
      setPayment_method(selectRows[0].payment);
    }
  }, [open, selectRows]);

  return (
    <AModal
      open={open}
      close={() => {
        setSelectRows([]);
        close();
      }}
      title={
        <>
          <DollarCircleOutlined /> Baixar/Pagar Contas
        </>
      }
      onOk={handleEditSubmit}
      width={500}
    >
      <Typography.Paragraph type="secondary">
        Você está baixando <b>{selectRows.length}</b> conta(s).
      </Typography.Paragraph>
      <Divider style={{ margin: '12px 0' }} />

      <Form layout="vertical">
        <Form.Item label="Data do Pagamento" required>
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            value={payment_date ? dayjs(payment_date) : null}
            onChange={(_, dateStr) => {
              const [day, month, year] = String(dateStr).split('/');
              setPayment_date(`${year}-${month}-${day}`);
            }}
            allowClear={false}
          />
        </Form.Item>
        <Form.Item label="Forma de Pagamento" required>
          <ASelect
            value={payment_method}
            placeholder="Selecione..."
            onChange={setPayment_method}
            options={paymentsMethods.map((p) => ({
              label: p.description,
              value: p.id,
            }))}
          />
        </Form.Item>
      </Form>
    </AModal>
  );
};

export default FinancialPayment;
